import React, { memo, useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import * as tus from 'tus-js-client';

import { areEqual } from 'Utils/equalityChecks';
import { Dropzone } from 'Components/RocketScan';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from 'Containers/PhotoErrorToast/actions';
import { useUser } from 'Context/User';
import { UserModel } from 'Containers/User/Models/UserModel';
import { chunkArray, generateUUID } from 'Utils/helpers';
import { GeneralToast } from 'Components/Toast';
import {
  albumTypesSelector,
  projectAddressSelector,
  projectAliasSelector,
  projectSelector,
  propertySelector,
} from 'Containers/RocketScan/selectors';
import { useRoomFunctions } from 'Context/RocketScan';
import { sendPhotosST } from 'Utils/serviceTitanSyncAssets';
import { roomsSelector } from 'Containers/RocketScan/RoomsView/Rooms/selectors';
import { locationSelector } from 'Containers/RocketScan/MultiUnit/Locations/selectors';
import { userFeatureFlagsSelector } from 'Containers/User/selector';
import {
  buildImageProcessorPayload,
  createRoomImageProcessorAssembly,
  getProcessingServiceConfig,
} from 'Utils/imageProcessor';

interface Props {
  roomId: number;
  albumId?: number;
  hasImages: boolean;
  editable: boolean;
  onUploadStart?: (files: File[], uploadId: string) => void;
  onUploadComplete?: (success: boolean, uploadId: string) => void;
}

function DropzoneContainer({ roomId, albumId, hasImages, editable, onUploadStart, onUploadComplete }: Props) {
  const dispatch = useDispatch();
  const project = useSelector(projectSelector, areEqual);
  const alias = useSelector(projectAliasSelector, areEqual);
  const projectAddress = useSelector(projectAddressSelector, areEqual);
  const property = useSelector(propertySelector, areEqual);
  const rooms = useSelector(roomsSelector, areEqual);
  const albums = useSelector(albumTypesSelector, areEqual);
  const location = useSelector(locationSelector, areEqual);
  const { imageProcessor } = useSelector(userFeatureFlagsSelector, areEqual);
  const [showError, setShowError] = useState(false);
  const [errorFileName, setErrorFileName] = useState('');
  const [processingErrorMessage, setProcessingErrorMessage] = useState('');
  const [showProcessingError, setShowProcessingError] = useState(false);

  const user: UserModel = useUser();

  const {
    triggerImageProcessorCheck,
    registerImageProcessorAssembly,
    enableAlbumLocally,
  }: any = useRoomFunctions();

  const maxParallelUploads = useMemo(() => 5, []);
  const TUS_VERSION = '1.0.0';
  const TUS_CHUNK_SIZE_BYTES = 1 * 1024 * 1024; // 1MB chunks to stay under gateway limits

  const logProcessing = useCallback((message: string, context: Record<string, unknown> = {}) => {
    // eslint-disable-next-line no-console
    console.info('[image-processor]', message, context);
  }, []);

  const showUploadError = useCallback((message: string) => {
    setProcessingErrorMessage(message);
    setShowProcessingError(true);
  }, []);

  const hideUploadError = useCallback(() => setShowProcessingError(false), []);

  const buildTusMetadata = useCallback((file: File, assemblyId: string) => {
    const metadata = {
      filename: file.name,
      content_type: file.type,
      assembly_id: assemblyId,
    };

    return Object.entries(metadata)
      .map(([key, value]) => `${key} ${btoa(String(value))}`)
      .join(',');
  }, []);

  const createTusUploadSession = useCallback(
    async (endpoint: string, file: File, assemblyId: string, apiKey?: string) => {
      const headers: Record<string, string> = {
        'Tus-Resumable': TUS_VERSION,
        'Upload-Length': `${file.size}`,
        'Upload-Metadata': buildTusMetadata(file, assemblyId),
      };

      if (apiKey) {
        headers['x-api-key'] = apiKey;
      }

      logProcessing('Creating TUS upload session', { endpoint, fileName: file.name, size: file.size });
      const response = await axios.post(endpoint, null, { headers });
      const locationHeader = response.headers?.location || (response.headers as any)?.Location;
      const uploadUrl = locationHeader || (response.data as any)?.upload_url;

      if (!uploadUrl) {
        throw new Error('TUS creation did not return an upload URL.');
      }

      logProcessing('TUS session created', { uploadUrl, status: response.status });
      return uploadUrl;
    },
    [TUS_VERSION, buildTusMetadata, logProcessing]
  );

  const uploadFileWithTus = useCallback(
    async (uploadUrl: string, file: File, assemblyId: string, apiKey?: string) => {
      const sessionUploadUrl = await createTusUploadSession(uploadUrl, file, assemblyId, apiKey);

      return new Promise<void>((resolve, reject) => {
        logProcessing('Starting TUS upload', { fileName: file.name, size: file.size, type: file.type });
        const upload = new tus.Upload(file, {
          uploadUrl: sessionUploadUrl,
          chunkSize: TUS_CHUNK_SIZE_BYTES,
          metadata: {
            filename: file.name,
            content_type: file.type,
            assembly_id: assemblyId,
          },
          headers: {
            'X-Assembly-Id': assemblyId,
            ...(apiKey ? { 'x-api-key': apiKey } : {}),
          },
          retryDelays: [0, 1000, 2000],
          onError: (error) => {
            logProcessing('TUS upload failed', { fileName: file.name, error: error.message });
            reject(error);
          },
          onSuccess: () => {
            logProcessing('TUS upload success', { fileName: file.name, uploadUrl: upload.url });
            resolve();
          },
        });

        upload.start();
      });
    },
    [createTusUploadSession, logProcessing]
  );

  const uploadFilesWithLimit = useCallback(
    async (files: File[], uploadUrl: string, assemblyId: string, apiKey?: string) => {
      const batches = chunkArray(files, maxParallelUploads);
      let totalFailed = 0;
      let totalUploaded = 0;
      const failedFileNames: string[] = [];

      logProcessing('Starting batch uploads', {
        totalFiles: files.length,
        batchCount: batches.length,
        batchSize: maxParallelUploads,
      });

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        logProcessing(`Processing batch ${batchIndex + 1}/${batches.length}`, {
          filesInBatch: batch.map((f) => f.name),
        });

        const results = await Promise.allSettled(
          batch.map((file) => uploadFileWithTus(uploadUrl, file, assemblyId, apiKey))
        );

        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            totalFailed += 1;
            failedFileNames.push(batch[index].name);
          } else {
            totalUploaded += 1;
          }
        });

        logProcessing(`Batch ${batchIndex + 1} complete`, {
          uploaded: totalUploaded,
          failed: totalFailed,
          remaining: files.length - totalUploaded - totalFailed,
        });
      }

      if (totalFailed > 0) {
        logProcessing('Some uploads failed', { assemblyId, failedCount: totalFailed, failedFiles: failedFileNames });
        throw new Error(`Failed to upload ${totalFailed} file(s)`);
      }
    },
    [logProcessing, maxParallelUploads, uploadFileWithTus]
  );

  // on files drop or upload
  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: File[]) => {
      // show error toast
      if (rejectedFiles.length > 0) {
        dispatch(showToast());
      }

      if (acceptedFiles.length > 0) {
        enableAlbumLocally?.(albumId);
        const uploadId = generateUUID();
        onUploadStart?.(acceptedFiles, uploadId);

        try {
          const clonedFiles = acceptedFiles.map((file) => new File([file], file.name, { type: file.type }));

          // Should check if the project itself is a serviceTitan project
          if (user.st_technician_id) {
            const smallBatchArr = chunkArray(clonedFiles, 3);

            const finalBatch = [];
            const indexToBeRemoved = [];
            smallBatchArr.forEach((arr) => {
              const total = arr.reduce((acc, picture, idx) => {
                if (picture.size > 4000000) {
                  setErrorFileName(picture.name);
                  setShowError(true);
                  indexToBeRemoved.push(idx);
                  return acc;
                }
                return acc + picture.size;
              }, 0);
              const filterArr = arr.filter((_, idx) => !indexToBeRemoved.includes(idx));

              if (total > 4000000) {
                const brokenDownChunk = filterArr.map((file) => [file]);
                finalBatch.push(...brokenDownChunk);
              } else {
                finalBatch.push(filterArr);
              }
              indexToBeRemoved.length = 0;
            });

            const photoNameInfo = {
              address: projectAddress.replaceAll(' ', '_'),
              unit: property.name,
              room: rooms.find((room) => room.id === roomId || ''),
              album: albums.find((album) => album.id === albumId || ''),
              name: property.name === 'multiunit' ? location.name : '',
            };

            sendPhotosST(finalBatch, alias, photoNameInfo, user);
          }
          const projectId = project?.id;

          logProcessing('Upload started', {
            imageProcessorEnabled: imageProcessor,
            projectId,
            roomId,
            albumId,
            fileCount: acceptedFiles.length,
            fileNames: acceptedFiles.map((f) => f.name),
          });

          if (imageProcessor && projectId) {
            logProcessing('Step 1: Fetching processing service config');
            const processingConfig = await getProcessingServiceConfig();
            logProcessing('Step 1 complete: Got config', {
              hasUrl: !!processingConfig?.url,
              hasApiKey: !!processingConfig?.apiKey,
              service: processingConfig?.service,
            });

            const processingUrl = processingConfig?.url;

            if (!processingUrl) {
              throw new Error('Processing service URL is not configured.');
            }

            logProcessing('Step 2: Building payload');
            const payload = buildImageProcessorPayload({
              roomId,
              albumId,
              projectId,
              files: acceptedFiles,
            });
            logProcessing('Step 2 complete: Payload built', {
              assemblyId: payload.assembly_id,
              totalFiles: payload.total_files,
              bytesReceived: payload.bytes_received,
            });

            logProcessing('Step 3: Creating assembly via API');
            const assemblyResponse = await createRoomImageProcessorAssembly(roomId, payload);
            logProcessing('Step 3 complete: Assembly created', {
              responseAssemblyId: assemblyResponse?.assembly_id,
              hasProcessingUrl: !!assemblyResponse?.processing_url,
              hasApiKey: !!assemblyResponse?.api_key,
            });

            const assemblyId = assemblyResponse?.assembly_id || payload.assembly_id;
            const uploadUrl = assemblyResponse?.processing_url || processingUrl;
            const apiKey = assemblyResponse?.api_key || processingConfig?.apiKey;

            if (!assemblyId || !uploadUrl) {
              throw new Error('Processing service did not return upload details.');
            }

            // Subscribe immediately using the assembly we just created
            registerImageProcessorAssembly?.(assemblyId);
            triggerImageProcessorCheck?.();

            logProcessing('Step 4: Uploading files to processing service', {
              assemblyId,
              uploadUrl,
              fileCount: acceptedFiles.length,
            });
            await uploadFilesWithLimit(acceptedFiles, uploadUrl, assemblyId, apiKey);
            logProcessing('Step 4 complete: All files uploaded successfully', {
              assemblyId,
              fileCount: acceptedFiles.length,
            });
          } else {
            logProcessing('Upload blocked - feature not available', {
              imageProcessorEnabled: imageProcessor,
              hasProjectId: !!projectId,
            });
            showUploadError('Photo uploads are unavailable. Please contact support.');
            onUploadComplete?.(false, uploadId);
            return false;
          }

          onUploadComplete?.(true, uploadId);
        } catch (error) {
          const axiosMeta = axios.isAxiosError(error)
            ? {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                url: error.config?.url,
                method: error.config?.method,
              }
            : undefined;

          logProcessing('Upload FAILED', {
            roomId,
            albumId,
            projectId: project?.id,
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            axios: axiosMeta,
          });
          showUploadError('Could not upload photos. Please try again.');
          onUploadComplete?.(false, uploadId);
          return false;
        }
      }

      return true;
    },
    [
      project,
      projectAddress,
      property,
      rooms,
      albums,
      location,
      user,
      alias,
      albumId,
      roomId,
      dispatch,
      imageProcessor,
      uploadFilesWithLimit,
      showUploadError,
      triggerImageProcessorCheck,
    ]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ['image/jpeg', 'image/png'],
  });

  return (
    editable && (
      <>
        <Dropzone
          hasImages={hasImages}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          showError={showError}
          setShowError={setShowError}
          fileName={errorFileName}
        />
        <GeneralToast
          id="processing-upload-error"
          show={showProcessingError}
          message={processingErrorMessage}
          closeToast={hideUploadError}
          success={false}
        />
      </>
    )
  );
}

DropzoneContainer.defaultProps = {
  albumId: undefined,
};

const DropzoneContainerMemo = memo(DropzoneContainer, areEqual);

export { DropzoneContainerMemo as Dropzone };
